"use client"

import { useRef } from "react"
import CookieConsent from "react-cookie-consent"

const GDPR_COOKIE_NAME = "tamdagroupGdpr"
const GDPR_COOKIE_EXPIRE_DAYS = 150

type GdprCopy = {
  title: string
  body: string
  accept: string
  decline: string
}

const COPY: Record<string, GdprCopy> = {
  vi: {
    title: "Chúng tôi quan tâm đến sự riêng tư của bạn",
    body: "Chúng tôi, công ty TAMDA GROUP, sử dụng cookie để đảm bảo chức năng của trang web và, với sự đồng ý của bạn, cũng để cá nhân hóa nội dung trang web của chúng tôi. Bằng cách nhấp vào nút 'Tôi hiểu', bạn đồng ý với việc sử dụng cookie và truyền dữ liệu về hành vi của trang web để hiển thị quảng cáo được nhắm mục tiêu trên các mạng xã hội và mạng quảng cáo trên các trang web khác.",
    accept: "Tôi hiểu",
    decline: "Từ chối",
  },
  en: {
    title: "We care about your privacy",
    body: "We, TAMDA GROUP, use cookies to ensure the website's functionality and, with your consent, to personalize our website's content. By clicking the 'I understand' button, you agree to the use of cookies and the transmission of website behavior data to display targeted advertising on social networks and advertising networks on other websites.",
    accept: "I understand",
    decline: "Decline",
  },
  cs: {
    title: "Záleží nám na vašem soukromí",
    body: "My, společnost TAMDA GROUP, používáme cookies k zajištění funkčnosti webových stránek a s vaším souhlasem také k personalizaci obsahu našich webových stránek. Kliknutím na tlačítko 'Rozumím' souhlasíte s používáním cookies a přenosem dat o chování na webu za účelem zobrazování cílené reklamy na sociálních sítích a reklamních sítích na jiných webových stránkách.",
    accept: "Rozumím",
    decline: "Odmítnout",
  },
}

function GdprConsentContent({
  locale,
  onAccept,
  onDecline,
}: {
  locale: string
  onAccept: () => void
  onDecline: () => void
}) {
  const t = COPY[locale] ?? COPY.vi

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t.title}
      style={{ fontFamily: "Roboto, Arial, sans-serif" }}
      className="fixed inset-x-0 bottom-0 z-[10002] bg-[#172639] shadow-[0_-2px_4px_rgba(0,0,0,0.15)]"
    >
      <div className="mx-auto flex max-w-[1035px] flex-col px-5 py-8 md:flex-row md:justify-between md:px-0 md:py-[30px]">
        <div className="flex flex-col md:min-w-0 md:flex-1 md:mr-[150px]">
          <p className="mb-5 text-[18px] font-semibold text-[#F8F8F8]">{t.title}</p>
          <p className="mb-5 text-[14px] font-light leading-[25px] text-white">{t.body}</p>
        </div>
        <div className="flex flex-col gap-4 md:shrink-0 md:justify-center">
          <button
            type="button"
            onClick={onAccept}
            style={{ fontFamily: "Lato, sans-serif" }}
            className="relative h-9 w-full overflow-visible rounded-[4px] bg-[#18AF4E] px-[18px] text-[14px] font-semibold leading-none text-white transition-all duration-500 before:absolute before:inset-0 before:z-0 before:rounded-[4px] before:bg-[#18AF4E] before:transition-all before:duration-300 after:absolute after:inset-0 after:z-0 after:scale-125 after:rounded-lg after:border-2 after:border-[#18AF4E] after:opacity-0 after:transition-all after:duration-300 hover:brightness-110 hover:before:scale-50 hover:before:opacity-0 hover:after:scale-100 hover:after:opacity-100 active:translate-y-[1px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#51CF66] md:w-auto"
          >
            <span className="relative z-10">{t.accept}</span>
          </button>
          <button
            type="button"
            onClick={onDecline}
            style={{ fontFamily: "Lato, sans-serif" }}
            className="h-9 w-full rounded-[4px] bg-[#F3F3F3] px-[18px] text-[14px] font-semibold leading-none text-[#333333] transition-all duration-500 hover:text-[#18AF4E] hover:brightness-[1.02] active:translate-y-[1px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#51CF66] md:w-auto"
          >
            {t.decline}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function GdprConsentBanner({ locale }: { locale: string }) {
  const consentRef = useRef<CookieConsent>(null)

  return (
    <CookieConsent
      ref={consentRef}
      location="bottom"
      buttonText="Accept!!"
      declineButtonText="Reject"
      cookieName={GDPR_COOKIE_NAME}
      enableDeclineButton
      expires={GDPR_COOKIE_EXPIRE_DAYS}
      sameSite="lax"
      cookieSecurity
    >
      <GdprConsentContent
        locale={locale}
        onAccept={() => consentRef.current?.accept()}
        onDecline={() => consentRef.current?.decline()}
      />
    </CookieConsent>
  )
}
